import { IssueLifecycleState } from '../../models/issue';
import { IssueLocationState } from '../../query/issue-fleet-row';

// What the row renders, whichever surface it appears on.
//
// Both row builders satisfy this structurally, so neither surface needs an adapter: the
// per-asset builder produces it from the full Issue, and the fleet builder produces it
// from the projected row. The fields a narrow surface never shows are optional, which is
// what lets one component serve every surface without either caller pretending to hold
// data it does not have.
export interface IssueRowDisplay {
  id: string;
  displayName: string;
  // Shown next to the name. Empty when the Issue has none; the name then stands alone.
  description?: string;
  lifecycleState: IssueLifecycleState;
  lifecycleCss: string;
  severity: number;
  confidencePct: number;
  confidenceMuted: boolean;
  signalCount: number;
  reopened: boolean;
  dvirOrigin: boolean;
  assigneeInitials: string | null;
  assigneeName: string | null;
  lastActivityUtc: string;
  // When the Issue opened. The sheet shows how long it has been open; the asset card does
  // not, so the field is optional on the shared contract.
  openedAtUtc?: string;

  // The sheet only. A panel renders inside one asset's screen, which has already named
  // it, so repeating the asset on every row there would be noise.
  assetName?: string;
  assetVin?: string;
  // The problem's stable identity. Not shown on the row — it is an identifier rather
  // than something anyone scans — but it rides in the tooltip, because support asks for
  // it by name.
  fingerprint?: string;
  // The catalog's grouping for the problem, shown beside the title. Empty for an
  // uncatalogued fingerprint.
  category?: string;
  locationState?: IssueLocationState;
  currentShopName?: string;
  awayFromHome?: boolean;
  hasCase?: boolean;
  unassigned?: boolean;
}

// Where the row is being shown, not what it should draw. A caller names its surface and
// the row decides the columns, so two surfaces cannot drift into two designs. `sheet` is
// the full-width fleet list; `panel` is a narrow surface inside one asset's screen. The
// variant is fixed before the viewport renders, so a list holds one of them and the row
// height stays uniform within it.
export const IssueRowVariants = { Sheet: 'sheet', Panel: 'panel' } as const;
export type IssueRowVariant = typeof IssueRowVariants[keyof typeof IssueRowVariants];
